import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { format } from 'date-fns'
import styled from '@emotion/styled'

import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'

import axios from '../../dataClient/dataClient'
import { usePolling } from '../../hooks/usePolling'
import { theme } from '../../themes/theme'
import { parseDateOnly } from '../../util/foiFormatting'
import { addCommunicationToBills, formatBillFromServer } from '../../util/billFormatting'
import { formatUsersToAssignDropdownOptions } from '../../util/userFormatting'
import { formatCategoriesForDisplay, formatCategoryForDisplay } from '../../util/categoryFormatting'
import { PageWithTitle, HeaderWithButtons, FlexColumn, FlexRow, LightBGContainer, PrimaryBGContainer, FlexEndContainer } from '../styles/Containers'
import { PageTitle } from '../styles/Text'
import { sortByDateKey } from '../../util/sorting'
import { completedFoiStatuses, inProgressFoiStatuses, responsePageReadOnlyStatuses } from '../../util/foiConsts'
import { billTypeMap } from '../../util/billingConsts'
import { FOI_RESPONSE_TAB_QUERY_VALUES, DUE_DATE_BUSINESS_DAYS } from '../../util/consts'
import { formatCategoryWorkflow } from '../../util/workflowFormatting'
import { getWorkflowRefreshAction, WORKFLOW_REFRESH_ACTIONS } from '../../util/workflowRefresh'
import { config } from '../../config'
import foiAccessMethod, { ACCESS_METHOD_TYPE } from '../../util/foiAccessMethod'
import Loading from '../common/Loading'
import LoadingError from '../common/LoadingError'
import DueDateList from '../foiResponse/DueDateList'
import FOINavigationBar, { isFoiResponseTabDisabled } from '../foiResponse/FOINavigationBar'
import FOIEditableStatus from '../foiResponse/FOIEditableStatus'
import FOIRequestView from '../foiResponse/FOIRequestView'
import CountdownBar from '../foiResponse/CountdownBar'
import SubmitFOIResponseButton from '../foiResponse/SubmitFOIResponseButton'
import ResponseConfirmation from '../common/ResponseConfirmation'
import ActivityLog from '../activityLog/ActivityLog'
import AcceptFoiButton from '../foiResponse/AcceptFoiButton'
import NotAValidRequestButton from '../foiResponse/NotAValidRequestButton'
import CloseFoiButton from '../foiResponse/CloseFoiButton'
import ResendResponseLinkButton from '../foiResponse/ResendResponseLinkButton'
import DownloadResponseButton from '../common/DownloadResponseButton'
import ResponseTab from '../foiDocumentsAndResponse/ResponseTab'
import BillingTab from '../foiResponse/BillingTab'
import Categories from '../records/Categories'
import LettersTab from '../letters/LettersTab'
import ResponsePackageConfirmation from '../foiResponse/ResponsePackageConfirmation'
import Dropdown from '../inputFields/Dropdown'
import CommunicationsTab from '../communications/CommunicationsTab'
import RequestSearch from '../records/recordsSearch/RequestSearch'

const Sticky = styled(FlexColumn)`
  min-height: calc(100vh - 40px);
  height: max-content;
  position: sticky;
  top: 20px;
`

const StickyColumnContent = styled(Sticky)`
  width: 25%;
  min-width: 300px;
`

const ConnectorLineContainer = styled(Sticky)`
  height: calc(100vh - 40px);
  align-items: center;
  gap: 0;
`

const ConnectorLine = styled.div`
  height: 100%;
  width: 1px;
  background: ${theme.colors.sectionDivider};
`

const CollapseButton = styled.div`
  display: flex;
  align-items: center;

  background: ${theme.colors.background.white};
  border: 1px solid ${theme.colors.sectionDivider};
  border-radius: 50px;
  padding: ${props => (props.open ? '8px 7px 7px 8px' : '8px 3px 7px 12px')};

  -webkit-transition: all 0.3s ease;
  transition: all 0.3s ease;

  .MuiSvgIcon-root {
    height: 22px;
    width: 22px;
  }

  :hover {
    cursor: pointer;
    background: ${theme.colors.background.light};
  }
`

const TabContent = styled(FlexColumn)`
  min-height: 100vh;
`

const HeaderActionsRow = styled(FlexRow)`
  align-items: center;
  /* Keep request actions on the same row. */
  flex: 0 0 auto;
  flex-wrap: wrap;
  justify-content: flex-end;
`

// Keep the search beside the request heading.
const TitleGroup = styled(FlexRow)`
  align-items: center;
  flex: 1 1 auto;
  min-width: 0;
  gap: 14px;

  /* Let the search field shrink before the heading. */
  h1 {
    flex: 0 0 auto;
    white-space: nowrap;
  }
`

export { FOI_RESPONSE_TAB_QUERY_VALUES }

function FOIResponsePage({ accessMethodConfig = config.foi.request.accessMethod }) {
  const navigate = useNavigate()
  const { foiId } = useParams()
  const [searchParams] = useSearchParams()
  // Preselect a category from editor links and search results.
  const categoryIdQueryParam = searchParams.get('categoryId')
  // Open a linked file preview at the requested page.
  const [previewRequest, setPreviewRequest] = useState(() => {
    const fileId = searchParams.get('fileId')
    if (!fileId) return null
    const page = Number.parseInt(searchParams.get('page'), 10)
    return { fileId, categoryId: searchParams.get('categoryId'), page: Number.isInteger(page) && page > 0 ? page : null, nonce: 0 }
  })
  // Consume each preview request once so closed previews stay closed.
  const consumePreviewRequest = useCallback(() => setPreviewRequest(null), [])
  // Anchor search results to the full header row.
  const headerRowRef = useRef(null)
  const [submitted, setSubmitted] = useState(false)
  const [activeTab, setActiveTab] = useState('request')
  const [columnIsVisible, setColumnIsVisible] = useState(true)

  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState()
  const [foi, setFoi] = useState()
  const [accessMethod, setAccessMethod] = useState()
  const [responseFiles, setResponseFiles] = useState([])
  const [responseAttachment, setResponseAttachment] = useState()
  const [internalSummary, setInternalSummary] = useState()
  const [requestClassification, setRequestClassification] = useState()
  const [foiStatus, setFoiStatus] = useState()
  const [dueDates, setDueDates] = useState()
  const [activities, setActivities] = useState()
  const [accepting, setAccepting] = useState(false)

  const [users, setUsers] = useState()
  const [loadingUsers, setLoadingUsers] = useState(true)

  const [categories, setCategories] = useState([])
  const [archivedCategory, setArchivedCategory] = useState({ id: 'removed', name: 'Removed Files', numberOfFiles: 0, size: 0 })
  const [archivedCategoryFiles, setArchivedCategoryFiles] = useState()
  const [activeCategoryId, setActiveCategoryId] = useState()
  const [activeCategoryFiles, setActiveCategoryFiles] = useState()
  const [activeCategoryDuplicates, setActiveCategoryDuplicates] = useState()
  const [categoryErrorMessage, setCategoryErrorMessage] = useState()

  const [workflows, setWorkflows] = useState([])
  const [workflowsError, setWorkflowsError] = useState(false)

  const [loadingKeywords, setLoadingKeywords] = useState(true)
  const [allKeywords, setAllKeywords] = useState([])
  const [loadingDepartments, setLoadingDepartments] = useState(true)
  const [allDepartments, setAllDepartments] = useState([])

  const [communicationsLoading, setCommunicationsLoading] = useState(true)
  const [communicationTemplatesLoading, setCommunicationTemplatesLoading] = useState(true)
  const [communicationsError, setCommunicationsError] = useState(false)
  const [communicationsTab, setCommunicationsTab] = useState('DRAFT')
  const [isEditingCommunication, setIsEditingCommunication] = useState(false)
  const [communications, setCommunications] = useState()
  const [communicationTemplates, setCommunicationTemplates] = useState()
  const [activeCommunication, setActiveCommunication] = useState()

  const [publicConfig, setPublicConfig] = useState()

  const [lettersLoading, setLettersLoading] = useState(true)
  const [letterTemplatesLoading, setLetterTemplatesLoading] = useState(true)
  const [lettersError, setLettersError] = useState(false)
  const [letterTab, setLetterTab] = useState('DRAFT')
  const [isEditingLetter, setIsEditingLetter] = useState(false)
  const [activeLetter, setActiveLetter] = useState()
  const [letters, setLetters] = useState()
  const [letterTemplates, setLetterTemplates] = useState()

  const [bills, setBills] = useState([])
  const [billsLoading, setBillsLoading] = useState(true)
  const [billsError, setBillsError] = useState(false)
  const [editingBill, setEditingBill] = useState()

  const finalResponseLetterId = foi?.finalResponseLetterId ?? null
  const finalResponseLetter = (letters || []).find(letter => letter.id === finalResponseLetterId) ?? null

  const [foiVersions, setFoiVersions] = useState()
  const [activeFoiVersion, setActiveFoiVersion] = useState()
  const [loadingFoiVersions, setLoadingFoiVersions] = useState(true)

  const handleChangeColumnIsVisible = () => setColumnIsVisible(columnIsVisible => !columnIsVisible)

  function getFoiDetails(foiId, isRefresh = false) {
    if (!isRefresh) setLoading(true)

    axios
      .get(`/foi/${foiId}`)
      .then(({ data }) => {
        const formattedData = {
          ...data.data
        }
        formattedData.dueDates = formattedData.dueDates.map(d => {
          return { ...d, dueDate: parseDateOnly(d.dueDate) }
        })

        if (formattedData.activities) {
          formattedData.activities = formattedData.activities.map(activity => {
            return { ...activity, date: new Date(activity.date) }
          })
        }

        setAccessMethod(formattedData.accessMethod)
        setFoi({ ...formattedData })
        setFoiStatus(formattedData.status)
        setDueDates([...formattedData.dueDates])
        setActivities([...formattedData.activities])
        setResponseFiles([...formattedData.responseFiles])
        setResponseAttachment(formattedData.responseAttachment ? { ...formattedData.responseAttachment } : null)
        setInternalSummary([...formattedData.internalSummary])
        setRequestClassification({
          applicantType: formattedData.applicantType,
          keywords: [...formattedData.keywords],
          departments: formattedData.departments || []
        })
      })
      .catch(e => {
        console.error('Failed to retrieve details for FOI', foiId, e)
        if (e.response?.data?.message?.includes('foiId not found')) {
          setErrorMessage(['The provided FOI Request does not exist.'])
        } else {
          setErrorMessage([])
        }
      })
      .finally(() => {
        setLoading(false)
        setAccepting(false)
      })
  }

  function getActiveFoiVersion(foiId, versionId) {
    axios
      .get(`/foi/${foiId}/version/${versionId}`)
      .then(({ data }) => {
        const requestData = {
          ...data.data.snapshot,
          thirdPartyFiles: data.data.snapshot.files,
          id: versionId
        }
        delete requestData.files

        requestData.addresses = requestData.addresses.map(a => {
          return {
            ...a,
            files: a.files.map(f => {
              return { ...f, usage: 'ADDRESS' }
            })
          }
        })

        setActiveFoiVersion(requestData)
        setLoadingFoiVersions(false)
      })
      .catch(e => {
        console.error('Failed to retrieve active foi request', e)
        setErrorMessage([])
        setLoadingFoiVersions(false)
      })
  }

  function getFoiVersions(foiId) {
    axios
      .get(`/foi/${foiId}/version`)
      .then(({ data }) => {
        const withDates = data.data.map(v => {
          return { ...v, createdAt: new Date(v.createdAt) }
        })
        const sortedVersions = withDates.sort(sortByDateKey('createdAt'))
        const formattedVersions = sortedVersions.map(v => {
          return { id: v.id, name: format(v.createdAt, 'MMM-dd-yyyy hh:mm a'), isMostRecent: v.id === sortedVersions[0].id }
        })

        setFoiVersions(formattedVersions)
        getActiveFoiVersion(foiId, formattedVersions[0].id)
      })
      .catch(e => {
        console.error('Failed to retrieve foi versions', e)
        setErrorMessage([])
        setLoadingFoiVersions(false)
      })
  }

  function getUsers() {
    axios
      .get(`/users`)
      .then(({ data }) => setUsers(data.data))
      .catch(e => {
        console.error('Failed to retrieve users', e)
        setErrorMessage([])
      })
      .finally(() => setLoadingUsers(false))
  }

  function getArchivedFiles() {
    axios
      .get(`/foi/${foiId}/category/archive`)
      .then(({ data }) => {
        const fileData = data.data
        const files = fileData.map(f => {
          return {
            ...f,
            lastModified: new Date(f.updatedAt),
            createdAt: new Date(f.createdAt),
            archivedAt: f.archivedAt ? new Date(f.archivedAt) : null
          }
        })
        const size = fileData.reduce((acc, curr) => acc + curr.size, 0)

        setArchivedCategoryFiles(files)
        setArchivedCategory(prev => {
          return { ...prev, numberOfFiles: files.length, size }
        })
      })
      .catch(e => {
        console.error('Failed to retrieve archived files for FOI', foiId, e)
        setCategoryErrorMessage(['There was an error retrieving files for the category. Please refresh the page to try again.'])
      })
  }

  function getFoiCategories({ foiId, newActiveId, fetchSameCategoryFiles }) {
    axios
      .get(`/category?foiId=${foiId}`)
      .then(({ data }) => {
        const categories = data.data.categories.sort(sortByDateKey('updatedAt'))
        setCategories(categories)

        // Apply the linked category on the initial load.
        if (newActiveId === undefined) {
          const requestedCategory = categories.find(c => c.id === categoryIdQueryParam)
          if (requestedCategory) setActiveCategoryId(requestedCategory.id)
          else if (categories.length > 0) setActiveCategoryId(categories[0].id)
          else setActiveCategoryId(archivedCategory.id)
        } else if (newActiveId !== archivedCategory.id && !categories.some(c => c.id === newActiveId)) {
          setActiveCategoryId(categories[0]?.id ?? archivedCategory.id)
        } else if (newActiveId === activeCategoryId && activeCategoryId !== 'removed' && fetchSameCategoryFiles) {
          const categoryStatus = categories.find(c => c.id === newActiveId).status
          getCategoryFiles(newActiveId, categoryStatus)
        } else {
          setActiveCategoryId(newActiveId)
        }
      })
      .catch(e => {
        console.error('Failed to retrieve categories for FOI', foiId, e)
        if (e.response?.data?.message?.includes('foiId not found')) {
          setErrorMessage(['The provided FOI Request does not exist.'])
        } else {
          setCategoryErrorMessage(['There was an error loading the categories. Please refresh the page to try again.'])
        }
      })
  }

  function getCategoryFiles(categoryId, categoryStatus) {
    axios
      .get(`/category/${categoryId}/files?categoryStatus=${categoryStatus}`)
      .then(({ data }) => {
        const fileData = data.data.files
        const files = fileData.map(f => {
          return {
            ...f,
            lastModified: new Date(f.updatedAt),
            createdAt: new Date(f.createdAt),
            archivedAt: f.archivedAt ? new Date(f.archivedAt) : null
          }
        })

        setActiveCategoryFiles(files)
      })
      .catch(e => {
        console.error('Failed to retrieve files for category', categoryId, e)
        setCategoryErrorMessage(['There was an error retrieving files for the category. Please refresh the page to try again.'])
      })
  }

  function getActiveCategoryDuplicates(categoryId) {
    axios
      .get(`/category/${categoryId}/duplicateGroups`)
      .then(({ data }) => setActiveCategoryDuplicates(data.data.duplicateGroups))
      .catch(e => {
        console.error('Failed to retrieve duplicates for category', categoryId, e)
        setCategoryErrorMessage(['There was an error retrieving duplicate groups for the category. Please refresh the page to try again.'])
      })
  }

  function getAllKeywords() {
    axios
      .get(`/keyword`)
      .then(({ data }) => setAllKeywords(data.data))
      .catch(e => {
        console.error('Failed to retrieve keywords', e)
        setErrorMessage([])
      })
      .finally(() => setLoadingKeywords(false))
  }

  function getAllDepartments() {
    axios
      .get(`/department`)
      .then(({ data }) => setAllDepartments(data.data))
      .catch(e => {
        console.error('Failed to retrieve departments', e)
        setErrorMessage([])
      })
      .finally(() => setLoadingDepartments(false))
  }

  async function getAllWorkflows(foiId) {
    setWorkflowsError(false)
    await axios
      .get(`/foi/${foiId}/category/workflow`)
      .then(({ data }) => setWorkflows(data.data.workflows))
      .catch(e => {
        console.error('Failed to retrieve workflows for foi', foiId, e)
        setWorkflowsError(true)
      })
  }

  function getLetters(foiId) {
    axios
      .get(`/letters?foiId=${foiId}`)
      .then(({ data }) => {
        const formattedLetters = data.data.map(letter => {
          const updatedAt = new Date(letter.updatedAt)
          const sentAt = letter.sentAt ? new Date(letter.sentAt) : undefined
          return { ...letter, updatedAt, sentAt, date: letter.state === 'DRAFT' ? updatedAt : sentAt }
        })
        setLetters(formattedLetters)
      })
      .catch(e => {
        console.error('Failed to retrieve letters for FOI', foiId, e)
        setLettersError(true)
      })
      .finally(() => setLettersLoading(false))
  }

  function getLetterTemplates() {
    axios
      .get('/letter/letter-template')
      .then(({ data }) => setLetterTemplates(data.data))
      .catch(e => {
        console.error('Failed to retrieve letter templates', e)
        setLettersError(true)
      })
      .finally(() => setLetterTemplatesLoading(false))
  }

  function getActiveLetter(id, updateTab) {
    axios
      .get(`/letter/${id}`)
      .then(({ data }) => {
        const formattedData = {
          ...data.data,
          updatedAt: new Date(data.data.updatedAt),
          sentAt: data.data.sentAt ? new Date(data.data.sentAt) : undefined
        }
        if (updateTab && formattedData.foiId !== foiId) return

        setActiveLetter(formattedData)
        if (updateTab) setLetterTab(formattedData.state)
      })
      .catch(e => {
        console.error('Failed to retrieve letter', e)
      })
  }

  function getBills(foiId, nowEditing) {
    axios
      .get(`/bills?foiId=${foiId}`)
      .then(({ data }) => {
        const formattedBills = data.data.map(formatBillFromServer)
        setBills(formattedBills)

        if (nowEditing) {
          const bill = formattedBills.find(b => b.id === nowEditing)
          setEditingBill(bill)
        }
      })
      .catch(e => {
        console.error('Failed to retrieve bills for FOI', foiId, e)
        setBillsError(true)
      })
      .finally(() => setBillsLoading(false))
  }

  function getCommunications(foiId) {
    axios
      .get(`/communications?foiId=${foiId}`)
      .then(({ data }) => {
        const formattedCommunications = data.data.map(communication => {
          const updatedAt = new Date(communication.updatedAt)
          const sentAt = communication.sentAt ? new Date(communication.sentAt) : undefined
          return { ...communication, updatedAt, sentAt }
        })
        setCommunications(formattedCommunications)
      })
      .catch(e => {
        console.error('Failed to retrieve communications for FOI', foiId, e)
        setCommunicationsError(true)
      })
      .finally(() => setCommunicationsLoading(false))
  }

  function getCommunicationTemplates() {
    axios
      .get('/communication/communication-template')
      .then(({ data }) => setCommunicationTemplates(data.data))
      .catch(e => {
        console.error('Failed to retrieve communication templates', e)
        setCommunicationsError(true)
      })
      .finally(() => setCommunicationTemplatesLoading(false))
  }

  function getActiveCommunication(id, updateTab) {
    axios
      .get(`/communication/${id}`)
      .then(({ data }) => {
        const formattedData = {
          ...data.data,
          updatedAt: new Date(data.data.updatedAt),
          sentAt: data.data.sentAt ? new Date(data.data.sentAt) : undefined
        }

        if (updateTab && formattedData.foiId !== foiId) return

        setActiveCommunication(formattedData)
        if (updateTab) setCommunicationsTab(formattedData.state)
      })
      .catch(e => {
        console.error('Failed to retrieve communication', e)
      })
  }

  function getPublicConfig() {
    axios
      .get('/public-config')
      .then(({ data }) => setPublicConfig(data.data))
      .catch(e => console.error('Failed to retrieve public configs', e))
  }

  const patchFoiAssignee = selected => {
    const updatedValue = selected !== '' ? selected : null
    return axios.patch(`/foi/${foiId}`, { assignedTo: updatedValue }).then(() => getFoiDetails(foiId, true))
  }

  const handleSelectFoiVersion = versionId => getActiveFoiVersion(foiId, versionId)

  function handleUpdateFoiVersion(data) {
    setAccessMethod(data.data.accessMethod)
    getFoiVersions(foiId)
    getFoiDetails(foiId, true)
  }

  function resetLetters() {
    getLetters(foiId)
    setActiveLetter()
    setIsEditingLetter(false)
  }

  function handleSubmit() {
    setSubmitted(true)
    resetLetters()
  }

  function handleAcceptSuccess() {
    setAccepting(true)
    getFoiDetails(foiId, true)
  }

  function handleUpdateEditingBill(val) {
    if (val) {
      getBills(foiId, val)
    } else {
      setEditingBill()
    }
  }

  function handleBillUpdate() {
    getBills(foiId)
    getFoiDetails(foiId, true)

    refreshCommunications()
  }

  function handleUploadFiles(uploadedToId) {
    setActiveCategoryFiles()
    getFoiCategories({ foiId, newActiveId: uploadedToId, fetchSameCategoryFiles: true })
    getFoiDetails(foiId, true)
  }

  function handleRemoveOrRestoreFile(newActiveId = activeCategoryId) {
    getFoiCategories({ foiId, newActiveId, fetchSameCategoryFiles: true })
    getArchivedFiles()
    getFoiDetails(foiId, true)
  }

  // Refresh file names and ordering after a rename.
  function handleRenameFile() {
    getFoiCategories({ foiId, newActiveId: activeCategoryId, fetchSameCategoryFiles: true })
  }

  function refreshAfterWorkflowUpdate(workflow, categoryId, options = {}) {
    const refreshAction = getWorkflowRefreshAction(workflow, options)

    if (refreshAction === WORKFLOW_REFRESH_ACTIONS.NONE) return

    if (refreshAction === WORKFLOW_REFRESH_ACTIONS.CATEGORY_FILES) {
      setActiveCategoryFiles()
      getFoiCategories({ foiId, newActiveId: categoryId, fetchSameCategoryFiles: true })
      return
    }

    if (refreshAction === WORKFLOW_REFRESH_ACTIONS.DUPLICATES) {
      getFoiCategories({ foiId, newActiveId: categoryId, fetchSameCategoryFiles: true })
      getActiveCategoryDuplicates(categoryId)
      return
    }

    getFoiCategories({ foiId, newActiveId: categoryId, fetchSameCategoryFiles: false })
  }

  function handleUpdateWorkflow(workflow) {
    const formattedWorkflow = formatCategoryWorkflow(workflow)

    refreshAfterWorkflowUpdate(formattedWorkflow, formattedWorkflow.categoryId)

    setWorkflows(prevWorkflows => {
      const indexToUpdate = prevWorkflows.findIndex(w => w.categoryId === formattedWorkflow.categoryId)
      if (indexToUpdate !== -1) {
        return prevWorkflows.map((w, i) => (i === indexToUpdate ? formattedWorkflow : w))
      }
      return [...prevWorkflows, formattedWorkflow]
    })

    getFoiDetails(foiId, true)
  }

  function handleSelectCategory(categoryId) {
    if (activeCategoryId !== categoryId) {
      setActiveCategoryFiles()
      setActiveCategoryId(categoryId)
    }
  }

  // Open search hits in their category's inline preview.
  function handleOpenSearchResult(result) {
    setPreviewRequest({
      fileId: result.fileId,
      filename: result.filename,
      categoryId: result.categoryId,
      page: result.page ?? null,
      nonce: Date.now()
    })
    navigate({ search: `?tab=${FOI_RESPONSE_TAB_QUERY_VALUES.deduplicationAndRedaction}&categoryId=${result.categoryId}` })
  }

  function handleAddRemoveResponseFiles(data) {
    setCategories(prevCategories => {
      const indexToUpdate = prevCategories.findIndex(c => c.id === data.category.id)
      return prevCategories.map((c, i) => (i === indexToUpdate ? { ...c, status: data.category.status, updatedAt: data.category.updatedAt } : c))
    })
    getCategoryFiles(data.category.id, data.category.status)

    if (data.category.status === 'ADDED_TO_RESPONSE') {
      const newFiles = [...data.files.files].map(f => {
        return { ...f, lastModified: f.updatedAt }
      })
      setResponseFiles(prev => [...prev, ...newFiles])
    } else {
      const idsToRemove = [...data.files.files].map(f => f.id)
      setResponseFiles(prev => {
        const files = prev.filter(f => !idsToRemove.includes(f.id))
        return [...files]
      })
    }

    getFoiDetails(foiId, true)
  }

  function refreshCommunications() {
    getCommunications(foiId)

    if (activeCommunication) getActiveCommunication(activeCommunication.id)
  }

  const handleUpdateIsEditingLetter = newVal => setIsEditingLetter(newVal)

  function handleUpdateActiveLetterTab(newVal) {
    setActiveLetter()
    setLetterTab(newVal)
  }

  function handleViewLetter(letterId, { refreshFoi = false } = {}) {
    getActiveLetter(letterId)

    if (refreshFoi) {
      getLetters(foiId)
      getFoiDetails(foiId, true)
      refreshCommunications()
    }
  }

  function handleEditLetter(letterId) {
    getActiveLetter(letterId)
    setIsEditingLetter(true)
  }

  function handleUpdateLetter(letter) {
    setIsEditingLetter(false)
    getActiveLetter(letter.id)
    getLetters(foiId)
    getFoiDetails(foiId, true)
  }

  function handleDeleteLetter() {
    getLetters(foiId)
    setActiveLetter()
    getFoiDetails(foiId, true)

    refreshCommunications()
  }

  function handleCreateDraftLetterFromTemplate(letter) {
    getLetters(foiId)
    getActiveLetter(letter.id)
    setLetterTab('DRAFT')
    setIsEditingLetter(true)
    getFoiDetails(foiId, true)
  }

  const handleUpdateIsEditingCommunication = newVal => setIsEditingCommunication(newVal)

  function handleUpdateActiveCommunicationTab(newVal) {
    setActiveCommunication()
    setCommunicationsTab(newVal)
  }

  function handleViewCommunication(communicationId, { refreshFoi = false } = {}) {
    getActiveCommunication(communicationId)

    if (refreshFoi) {
      getCommunications(foiId)
      getFoiDetails(foiId, true)
    }
  }

  function handleEditCommunication(communicationId) {
    getActiveCommunication(communicationId)
    setIsEditingCommunication(true)
  }

  function handleUpdateCommunication(communication) {
    setIsEditingCommunication(false)
    getActiveCommunication(communication.id)
    getCommunications(foiId)
    getFoiDetails(foiId, true)
  }

  function handleSendCommunication() {
    setActiveCommunication()
    setCommunicationsTab('SENT')
    getCommunications(foiId)
    getFoiDetails(foiId, true)

    // refresh attachments
    getLetters(foiId)
    setActiveLetter()
    getBills(foiId)
  }

  function handleDeleteCommunication() {
    getCommunications(foiId)
    setActiveCommunication()
    getFoiDetails(foiId, true)
  }

  function handleCreateDraftCommunication(communication) {
    getCommunications(foiId)
    getActiveCommunication(communication.id)
    setCommunicationsTab('DRAFT')
    setIsEditingCommunication(true)
    getFoiDetails(foiId, true)
  }

  const pendingWorkflows = workflows.filter(w => w.status === 'PENDING')
  const hasPendingWorkflows = pendingWorkflows.length > 0

  const { isPolling, stopPolling, startPolling } = usePolling(
    async () => {
      const updatedWorkflows = []

      for (const workflow of pendingWorkflows) {
        try {
          const response = await axios.get(`/category/${workflow.categoryId}/workflow`)
          const updatedWorkflow = response.data?.data

          if (workflow.status !== updatedWorkflow.status) {
            updatedWorkflows.push(updatedWorkflow)
          }
        } catch (err) {
          console.error(`Failed to poll workflow for category ${workflow.categoryId}:`, err)
        }
      }

      if (updatedWorkflows.length > 0) {
        const activeCategoryWorkflow = updatedWorkflows.find(w => w.categoryId === activeCategoryId)

        refreshAfterWorkflowUpdate(activeCategoryWorkflow, activeCategoryId, { fallbackToCategoryRefresh: true })

        setWorkflows(prevWorkflows => {
          const newWorkflows = [...prevWorkflows]
          updatedWorkflows.forEach(updated => {
            const index = newWorkflows.findIndex(w => w.workflowId === updated.workflowId)
            if (index !== -1) newWorkflows[index] = updated
          })
          return newWorkflows
        })

        getFoiDetails(foiId, true)
      }
    },
    {
      interval: 1000,
      onError: err => {
        console.error('Workflow polling error:', err)
      }
    }
  )

  useEffect(() => {
    if (!isPolling && hasPendingWorkflows) startPolling()
    if (isPolling && !hasPendingWorkflows) stopPolling()
  }, [hasPendingWorkflows, stopPolling, startPolling])

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
    getAllKeywords()
    getAllDepartments()
  }, [])

  const letterIdParam = searchParams.get('letterId')
  const billIdParam = searchParams.get('billId')
  const communicationIdParam = searchParams.get('communicationId')

  useEffect(() => {
    if (foiId) {
      getFoiDetails(foiId)
      getFoiCategories({ foiId })
      getArchivedFiles()
      getAllWorkflows(foiId)
      getFoiVersions(foiId)
      getLetters(foiId)
      getLetterTemplates()
      getUsers()
      getBills(foiId)
      getCommunications(foiId)
      getCommunicationTemplates()
      getPublicConfig()

      if (letterIdParam) getActiveLetter(letterIdParam, true)
      if (communicationIdParam) getActiveCommunication(communicationIdParam, true)
    }
  }, [foiId, letterIdParam])

  const tabQueryParam = searchParams.get('tab')
  useEffect(() => {
    if (!foiStatus) return

    if (
      tabQueryParam &&
      Object.values(FOI_RESPONSE_TAB_QUERY_VALUES).includes(tabQueryParam) &&
      !isFoiResponseTabDisabled(tabQueryParam, foiStatus)
    ) {
      setActiveTab(tabQueryParam)
    } else {
      setActiveTab('request')
    }
  }, [foiId, foiStatus, tabQueryParam])

  // Follow category changes from links and search results.
  useEffect(() => {
    if (!categoryIdQueryParam || categories.length === 0) return
    if (categoryIdQueryParam !== activeCategoryId && categories.some(c => c.id === categoryIdQueryParam)) {
      handleSelectCategory(categoryIdQueryParam)
    }
  }, [categoryIdQueryParam])

  useEffect(() => {
    if (activeCategoryId) {
      if (activeCategoryId !== 'removed') {
        const categoryStatus = categories.find(c => c.id === activeCategoryId).status
        getCategoryFiles(activeCategoryId, categoryStatus)
        if (categoryStatus === 'DUPLICATES_READY') getActiveCategoryDuplicates(activeCategoryId)
      }
    }
  }, [activeCategoryId])

  if (loading || loadingKeywords || loadingDepartments || loadingFoiVersions || loadingUsers) return <Loading />

  if (errorMessage !== undefined) {
    return <LoadingError message={errorMessage} />
  }

  if (submitted) {
    if (foiAccessMethod.requiresResponsePackage(accessMethod))
      return (
        <ResponsePackageConfirmation
          foiId={foiId}
          secondaryId={foi.secondaryId}
          fileList={responseFiles}
          letter={finalResponseLetter}
          accessMethod={accessMethod}
          accessMethodConfig={accessMethodConfig}
        />
      )
    return (
      <ResponseConfirmation requestType="foi" requestId={foiId} secondaryId={foi.secondaryId} fileList={responseFiles} letter={finalResponseLetter} />
    )
  }

  // Only show a preview in its source category.
  const categoryPreview = previewRequest && (!previewRequest.categoryId || previewRequest.categoryId === activeCategoryId) ? previewRequest : null

  return (
    <PageWithTitle gap="15px" padding="0 5%">
      <FlexRow>
        <FlexColumn width={columnIsVisible ? '72%' : '97%'} padding="50px 0 70px">
          <HeaderWithButtons ref={headerRowRef} data-testid="foi-response-header-row">
            <TitleGroup data-testid="foi-response-title-row">
              <PageTitle>{foi.secondaryId} Response</PageTitle>
            </TitleGroup>
            <HeaderActionsRow gap="15px">
              {(foiStatus === 'NEW' || foiStatus === 'AWAITING_APPLICATION_FEE') && !accepting && (
                <NotAValidRequestButton foiId={foiId} bills={bills} onSuccess={() => navigate('/foi/inprogress')} />
              )}
              {inProgressFoiStatuses.includes(foiStatus) && activeTab === 'request' && (
                <CloseFoiButton foiId={foiId} onSuccess={() => getFoiDetails(foiId, true)} />
              )}
              {foiStatus === 'NEW' && !accepting && <AcceptFoiButton foiId={foiId} onSuccess={handleAcceptSuccess} />}
              {!responsePageReadOnlyStatuses.includes(foiStatus) && activeTab === 'documentsAndResponse' && (
                <SubmitFOIResponseButton
                  foiId={foiId}
                  fileList={responseFiles}
                  finalResponseLetter={finalResponseLetter}
                  finalResponseLetterId={finalResponseLetterId}
                  letters={letters}
                  responseAttachment={responseAttachment}
                  bills={bills}
                  accessMethod={accessMethod}
                  accessMethodConfig={accessMethodConfig}
                  onSuccess={handleSubmit}
                />
              )}
              {completedFoiStatuses.includes(foiStatus) && activeTab === 'documentsAndResponse' && (
                <>
                  {foiAccessMethod.requiresResponsePackage(accessMethod) ? (
                    <FlexEndContainer>
                      <DownloadResponseButton
                        requestType="foi"
                        requestId={foiId}
                        secondaryId={foi.secondaryId}
                        fileList={responseFiles}
                        letter={finalResponseLetter}
                        label="Download Response Package"
                      />
                    </FlexEndContainer>
                  ) : (
                    <ResendResponseLinkButton foiId={foiId} onSuccess={() => getFoiDetails(foiId, true)} />
                  )}
                </>
              )}
              {activeTab === 'deduplicationAndRedaction' && (
                <RequestSearch key={foiId} foiId={foiId} anchorRef={headerRowRef} onOpenResult={handleOpenSearchResult} />
              )}
            </HeaderActionsRow>
          </HeaderWithButtons>
          <TabContent gap="15px">
            <LightBGContainer padding="19px 30px 18px">
              <FOINavigationBar activeTab={activeTab} onChange={setActiveTab} foiStatus={foiStatus} />
            </LightBGContainer>
            {(() => {
              switch (activeTab) {
                case 'documentsAndResponse':
                  return (
                    <ResponseTab
                      foiId={foiId}
                      letters={letters}
                      finalResponseLetterId={finalResponseLetterId}
                      responseAttachment={responseAttachment}
                      view={responsePageReadOnlyStatuses.includes(foiStatus) ? 'completed' : 'editable'}
                      responseFiles={responseFiles}
                      accessMethod={accessMethod}
                      accessMethodConfig={accessMethodConfig}
                      handleLettersUpdated={() => {
                        getFoiDetails(foiId, true)
                        getLetters(foiId)
                      }}
                      handleUpdateResponseFiles={updatedFiles => {
                        setResponseFiles(updatedFiles)
                        getFoiDetails(foiId, true)
                      }}
                    />
                  )

                case 'deduplicationAndRedaction':
                  return (
                    <>
                      {categoryErrorMessage === undefined && !workflowsError ? (
                        <LightBGContainer padding="30px">
                          <Categories
                            categories={formatCategoriesForDisplay(categories.concat(archivedCategory), workflows)}
                            foiId={foiId}
                            activeCategory={
                              activeCategoryId === 'removed'
                                ? archivedCategory
                                : formatCategoryForDisplay(
                                    categories.find(c => c.id === activeCategoryId),
                                    workflows
                                  )
                            }
                            files={activeCategoryId === 'removed' ? archivedCategoryFiles : activeCategoryFiles}
                            preview={categoryPreview}
                            onPreviewConsumed={consumePreviewRequest}
                            duplicateGroups={activeCategoryDuplicates}
                            disabled={responsePageReadOnlyStatuses.includes(foi.status)}
                            handleSelectCategory={handleSelectCategory}
                            handleUploadFiles={handleUploadFiles}
                            handleRemoveFile={handleRemoveOrRestoreFile}
                            handleRestoreFile={handleRemoveOrRestoreFile}
                            handleRenameFile={handleRenameFile}
                            handleUpdateWorkflow={handleUpdateWorkflow}
                            handleAddRemoveResponseFiles={handleAddRemoveResponseFiles}
                          />
                        </LightBGContainer>
                      ) : (
                        <>{categoryErrorMessage !== undefined ? <LoadingError message={categoryErrorMessage} /> : <LoadingError />}</>
                      )}
                    </>
                  )

                case 'billing':
                  return (
                    <>
                      {!billsLoading && !billsError && (
                        <BillingTab
                          foiId={foiId}
                          foiStatus={foiStatus}
                          bills={addCommunicationToBills(bills, communications)}
                          users={users}
                          requestorEmail={foi?.requestor?.email || ''}
                          editingBill={editingBill}
                          expandedBillId={billIdParam}
                          onBillUpdate={handleBillUpdate}
                          handleUpdateEditingBill={handleUpdateEditingBill}
                        />
                      )}
                      {billsLoading && (
                        <LightBGContainer padding="30px">
                          <Loading>Loading bills...</Loading>
                        </LightBGContainer>
                      )}
                      {billsError && (
                        <LightBGContainer padding="30px">
                          <LoadingError message={['There was an error loading the FOI bills. Please refresh the page to try again.']} />
                        </LightBGContainer>
                      )}
                    </>
                  )

                case 'letters':
                  return (
                    <LightBGContainer padding="30px">
                      {!lettersLoading && !letterTemplatesLoading && !lettersError && (
                        <LettersTab
                          foiId={foiId}
                          tab={letterTab}
                          isEditing={isEditingLetter}
                          activeLetter={activeLetter}
                          letters={letters}
                          templates={letterTemplates}
                          users={users}
                          handleUpdateIsEditing={handleUpdateIsEditingLetter}
                          handleUpdateActiveTab={handleUpdateActiveLetterTab}
                          handleViewLetter={handleViewLetter}
                          handleEditDraft={handleEditLetter}
                          handleUpdateLetter={handleUpdateLetter}
                          handleDeleteDraft={handleDeleteLetter}
                          handleCreateDraftFromTemplate={handleCreateDraftLetterFromTemplate}
                        />
                      )}
                      {(lettersLoading || letterTemplatesLoading) && <Loading>Loading letters...</Loading>}
                      {lettersError && (
                        <LoadingError message={['There was an error loading the FOI letters. Please refresh the page to try again.']} />
                      )}
                    </LightBGContainer>
                  )

                case 'communications':
                  return (
                    <LightBGContainer padding="30px">
                      {!communicationsLoading && !communicationTemplatesLoading && !communicationsError && (
                        <CommunicationsTab
                          foiId={foiId}
                          foiStatus={foiStatus}
                          tab={communicationsTab}
                          isEditing={isEditingCommunication}
                          hasEmail={!['', undefined, null].includes(foi?.requestor.email)}
                          activeCommunication={activeCommunication}
                          communications={communications}
                          templates={communicationTemplates}
                          letters={(letters || []).filter(l => l.state === 'DRAFT')}
                          bills={bills
                            .filter(b => b.billState === 'DRAFT')
                            .map(b => {
                              return { id: b.id, name: `${b.secondaryId} - ${billTypeMap[b.type]}`, status: b.workflowStatus }
                            })}
                          publicConfig={publicConfig}
                          handleUpdateIsEditing={handleUpdateIsEditingCommunication}
                          handleUpdateActiveTab={handleUpdateActiveCommunicationTab}
                          handleViewCommunication={handleViewCommunication}
                          handleEditDraft={handleEditCommunication}
                          handleUpdateCommunication={handleUpdateCommunication}
                          handleSendCommunication={handleSendCommunication}
                          handleDeleteDraft={handleDeleteCommunication}
                          handleCreateDraft={handleCreateDraftCommunication}
                        />
                      )}
                      {(communicationsLoading || communicationTemplatesLoading) && <Loading>Loading communications...</Loading>}
                      {communicationsError && (
                        <LoadingError message={['There was an error loading the FOI communications. Please refresh the page to try again.']} />
                      )}
                    </LightBGContainer>
                  )

                default:
                  return (
                    <FOIRequestView
                      foiId={foiId}
                      foiRequest={activeFoiVersion}
                      foiVersions={foiVersions}
                      internalSummary={internalSummary}
                      requestClassification={requestClassification}
                      allKeywords={allKeywords}
                      allDepartments={allDepartments}
                      editable={!responsePageReadOnlyStatuses.includes(foiStatus)}
                      onUpdateSummary={summary => {
                        setInternalSummary(summary)
                        getFoiDetails(foiId, true)
                      }}
                      onUpdateClassification={classification => {
                        setRequestClassification(classification)
                        getFoiDetails(foiId, true)
                      }}
                      onUpdateKeywords={setAllKeywords}
                      onSelectFoiVersion={handleSelectFoiVersion}
                      onUpdateFoiVersion={handleUpdateFoiVersion}
                    />
                  )
              }
            })()}
          </TabContent>
        </FlexColumn>
        <ConnectorLineContainer>
          <CollapseButton
            onClick={handleChangeColumnIsVisible}
            aria-label={columnIsVisible ? 'Collapse column' : 'Expand column'}
            open={columnIsVisible}>
            {columnIsVisible ? <ArrowForwardIosIcon /> : <ArrowBackIosIcon />}
          </CollapseButton>
          <ConnectorLine />
        </ConnectorLineContainer>
        {columnIsVisible && (
          <StickyColumnContent>
            <LightBGContainer padding="25px 30px">
              <FlexColumn gap="15px">
                <FOIEditableStatus
                  foiId={foiId}
                  status={foiStatus}
                  onUpdateStatus={newStatus => {
                    setFoiStatus(newStatus)
                    setFoi(prev => ({ ...prev, status: newStatus }))
                    getFoiDetails(foiId, true)
                  }}
                />
                <Dropdown
                  options={formatUsersToAssignDropdownOptions(users)}
                  defaultValue={foi.assignedTo || ''}
                  heading="Assigned To"
                  updateHeading="Update Assignee"
                  onSelectOption={patchFoiAssignee}
                />
              </FlexColumn>
            </LightBGContainer>
            {completedFoiStatuses.includes(foiStatus) ? (
              <>
                {dueDates &&
                  dueDates.find(d => d.type === 'REQUEST') &&
                  (() => {
                    const requestDueDate = dueDates.find(d => d.type === 'REQUEST')
                    const budget = Math.max(DUE_DATE_BUSINESS_DAYS, requestDueDate.daysLeft)
                    return <CountdownBar daysTaken={budget - requestDueDate.daysLeft} totalDays={budget} isCompleted />
                  })()}
              </>
            ) : (
              <PrimaryBGContainer padding="25px 30px">
                <DueDateList
                  dueDates={dueDates}
                  onUpdateDueDates={nextDueDates => {
                    setDueDates(nextDueDates)
                    getFoiDetails(foiId, true)
                  }}
                />
              </PrimaryBGContainer>
            )}
            <PrimaryBGContainer padding="25px 30px 22px">
              <ActivityLog foiId={foiId} activityList={activities} users={users || []} onUpdateActivities={setActivities} />
            </PrimaryBGContainer>
          </StickyColumnContent>
        )}
      </FlexRow>
    </PageWithTitle>
  )
}

FOIResponsePage.propTypes = {
  accessMethodConfig: PropTypes.shape({
    digitalLabel: PropTypes.string,
    physicalLabel: PropTypes.string,
    otherActiveType: PropTypes.shape({
      type: PropTypes.oneOf([ACCESS_METHOD_TYPE.OTHER_DIGITAL, ACCESS_METHOD_TYPE.OTHER_PHYSICAL, ACCESS_METHOD_TYPE.OTHER_PICKUP]).isRequired,
      label: PropTypes.string.isRequired,
      pricingDetails: PropTypes.string
    })
  })
}

export default FOIResponsePage
